import { useFontStore } from "@/stores/fonts";
import { useGeneralStore } from "@/stores/generalStore";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useMergeTagsStore } from "@/stores/mergeTags";
import { lockedApi, api } from "@/api";
import { useNotificationsStore } from "@/stores/notifications";
import { useI18n } from "vue-i18n";
import axios, { AxiosResponse } from "axios";
import { computed } from "vue";

type SaveResponse = {
  html: string;
};

type Params = {
  definition?: string;
  apiKey: any;
  templateId: string;
  hostname: any;
  userId: any;
  options: {
    minify: boolean | undefined;
    fonts: string;
    desktopFirst: boolean | undefined;
    language?: string;
    smartMergeTags?: { syntax: { start: string; end: string } };
  };
};

export default () => {
  const mjmlStore = useMjmlStore();
  const generalStore = useGeneralStore();
  const fontsStore = useFontStore();
  const mergeTagsStore = useMergeTagsStore();
  const notificationsStore = useNotificationsStore();
  const { t: $t } = useI18n();

  const params = computed<Params>(() => {
    return {
      definition: JSON.stringify(mjmlStore.getMjml()),
      apiKey: mjmlStore.authorize.apiKey,
      templateId: mjmlStore.templateId,
      hostname: mjmlStore.authorize.hostname,
      userId: mjmlStore.authorize.userId,
      options: {
        minify: generalStore.htmlMinified,
        fonts: JSON.stringify(fontsStore.fontsWithUrl),
        desktopFirst: generalStore.htmlDesktopFirst,
        emailServiceProvider: generalStore.emailServiceProvider || "mailchimp",
        variants: {
          enrichWithConditions: true,
        },
        smartMergeTags: mergeTagsStore.smartMergeTagsForRequest,
      },
    };
  });

  async function saveToExternalServer(
    saveApiEndpoint: string,
    data: AxiosResponse<SaveResponse>,
    params: Omit<Params, "definition">
  ) {
    try {
      await axios.post(saveApiEndpoint, {
        ...params,
        ...{
          html: data.data.html,
          json: JSON.stringify(mjmlStore.getMjml()),
        },
      });
    } catch (e) {
      notificationsStore.createNotification({
        type: "error",
        expectSideEffect: true,
        text: $t("layout.saving_error"),
      });

      throw Error("Error while saving to external server");
    }
  }

  async function postToServer(lang?: string) {
    try {
      if (lang && typeof lang === "string") {
        params.value.options.language = lang;
      }
      const data = await axios.post<SaveResponse>(
        lockedApi.PREVIEW_SAVE,
        params.value
      );

      /*
       *  This is a case when user is saving HTML directly to their server
       *  using option in API, this feature is undocumented and should be flagged
       *  as legacy and removed in the future
       */
      if (api.SAVE) {
        delete params.value.definition;
        await saveToExternalServer(api.SAVE, data, params.value);
      }

      mjmlStore.edittedWithoutSave = false;
      return data;
    } catch (e) {
      notificationsStore.createNotification({
        type: "error",
        text: $t("layout.saving_error"),
      });

      throw new Error(String(e));
    }
  }

  async function save(lang?: string) {
    generalStore.saving = true;

    try {
      const data = await postToServer(lang);

      notificationsStore.createNotification({
        type: "success",
        expectSideEffect: true,
        text: $t("layout.saved"),
      });

      const mutations =
        mjmlStore.mjml.langs?.map((lang) => {
          return {
            lang: lang.key,
            primary: lang.primary,
          };
        }) || [];

      const syncedSections = mjmlStore.getSyncedSectionsUsage;

      const message = {
        type: "callback",
        action: "onSave",
        data: {
          args: [
            mjmlStore.getMjml(),
            data.data.html,
            mutations,
            syncedSections,
          ],
        },
      };

      window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");

      return { json: mjmlStore.getMjml(), html: data.data.html };
    } catch (e) {
      console.error(e);
    } finally {
      generalStore.saving = false;
    }
  }

  async function saveAndClose() {
    generalStore.savingAndClosing = true;

    try {
      const data = await postToServer();

      const mutations =
        mjmlStore.mjml.langs?.map((lang) => {
          return {
            lang: lang.key,
            primary: lang.primary,
          };
        }) || [];

      const syncedSections = mjmlStore.getSyncedSectionsUsage;

      const message = {
        type: "callback",
        action: "onSaveAndClose",
        data: {
          args: [
            mjmlStore.getMjml(),
            data.data.html,
            mutations,
            syncedSections,
          ],
        },
      };
      window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");

      return { json: mjmlStore.getMjml(), html: data.data.html };
    } catch (e) {
      console.error(e);
    } finally {
      generalStore.savingAndClosing = false;
    }
  }

  return {
    saveAndClose,
    save,
  };
};
